import { deriveResource, ParamsOf } from 'pulse/resources';
import { keyBy } from '../../utils/keyBy';
import { PropertyGroups } from '../PropertyGroups';
import { stringCollator } from '../../utils/stringCollator';
import type { PropertyDefinition as PropertyDefinitionType } from 'inbound-db-properties-service-client/InboundDbPropertiesService-Unified/types';

export const PropertyDefinitions = deriveResource(
  'hs-resources:PropertyDefinitions'
)
  .from(PropertyGroups)
  .pull(({ other }) =>
    other.data
      .flatMap((group) => group.propertyDefinitions ?? [])
      .sort((a, b) =>
        stringCollator.compare(
          a.property.label || a.property.name,
          b.property.label || b.property.name
        )
      )
  )
  .push(({ self, other }) => {
    if (other.data) {
      const propertiesMap = keyBy(
        self.data,
        (definition) => definition.property.groupName,
        (definition, __index, props = [] as PropertyDefinitionType[]) => {
          props.push(definition);
          return props;
        }
      );

      for (const group of other.data) {
        group.propertyDefinitions = propertiesMap[group.name];
      }
    }
  })
  .register();

export const PropertyDefinitionsMap = deriveResource(
  'hs-resources:PropertyDefinitionsMap'
)
  .from(PropertyDefinitions)
  .pull(({ other }) =>
    keyBy(other.data, (definition) => definition.property.name)
  )
  .push(({ self }) => Object.values(self.data))
  .register();

export type PropertyDefinitionParams = ParamsOf<typeof PropertyDefinitions> & {
  propertyName: string;
};

export const PropertyDefinition = deriveResource(
  'hs-resources:PropertyDefinition'
)
  .from(PropertyDefinitionsMap)
  .mapParams(
    ({
      objectTypeId,
      showHighlySensitiveProperties,
    }: PropertyDefinitionParams) => ({
      objectTypeId,
      showHighlySensitiveProperties,
    })
  )
  .mergeArgs(['propertyName'])
  .pull(({ self, other }) => {
    const property = other.data[self.params.propertyName];

    if (!property) {
      throw new Error(`Property ${self.params.propertyName} not found`);
    }

    return property;
  })
  .push(({ self, other }) => {
    if (other.data) {
      other.data[self.params.propertyName] = self.data;
    }
  })
  .register();
